import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { MUSIC_API_URL } from '../config';

const API_URL = MUSIC_API_URL;

function useTabTitle(title) {
  useEffect(() => {
    document.title = title;
  }, [title]);
}

function MusicMainPage() {
  const navigate = useNavigate();
  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchForm, setSearchForm] = useState({
    title: '',
    composer: '',
    arranger: '',
    publishers_number: '',
    level: '',
    ensemble: '',
    required_instruments: '',
    optional_instruments: '',
    comments: '',
    group: '',
    event: '',
    date: ''
  });
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedOctaves, setSelectedOctaves] = useState([]);

  useTabTitle('Music Library - Hancock Handbells');

  useEffect(() => {
    fetchPieces();
  }, []);

  const fetchPieces = async () => {
    try {
      const response = await fetch(`${API_URL}/pieces`);
      if (response.ok) {
        const data = await response.json();
        setPieces(data);
      }
    } catch (e) {
      console.error('Failed to fetch pieces:', e);
    } finally {
      setLoading(false);
    }
  };

  const searchPieces = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(searchForm).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      // Don't send level to backend if we have selected levels - we'll filter client-side
      // to support multiple levels and fuzzy matching (3 matches "3", "3+", "3-4", etc.)
      if (selectedLevels.length > 0) {
        params.delete('level'); // Remove if it was set in searchForm
      }
      
      // Don't send octave params to backend - we'll filter client-side
      // to properly handle range matching
      params.delete('min_octaves');
      params.delete('max_octaves');
      
      const response = await fetch(`${API_URL}/pieces/search?${params.toString()}`);
      if (response.ok) {
        let data = await response.json();
        
        // Filter results client-side for multiple levels with fuzzy matching
        // Level 3 should match "3", "3+", "3-4", "3-5", etc.
        if (selectedLevels.length > 0) {
          data = data.filter(piece => {
            if (!piece.level) return false;
            const levelStr = piece.level.toString().trim();
            
            return selectedLevels.some(selectedLevel => {
              const selectedNum = parseInt(selectedLevel);
              
              // Exact match
              if (levelStr === selectedLevel) return true;
              
              // Check if level starts with selected level followed by non-digit
              // (e.g., "3" matches "3+", "3-4", "3-5", but not "13" or "30")
              if (levelStr.startsWith(selectedLevel)) {
                const nextChar = levelStr[selectedLevel.length];
                if (!nextChar || !nextChar.match(/\d/)) {
                  return true; // Matches "3+", "3-", "3-4", etc.
                }
              }
              
              // Check if level is a range that includes the selected level
              // (e.g., "3-5" matches searches for "3", "4", or "5")
              if (levelStr.includes('-')) {
                const parts = levelStr.split('-');
                if (parts.length >= 2) {
                  const minStr = parts[0].replace(/[^0-9]/g, '');
                  const maxStr = parts[1].replace(/[^0-9]/g, '');
                  if (minStr && maxStr) {
                    const minNum = parseInt(minStr);
                    const maxNum = parseInt(maxStr);
                    if (!isNaN(minNum) && !isNaN(maxNum)) {
                      return selectedNum >= minNum && selectedNum <= maxNum;
                    }
                  }
                }
              }
              
              return false;
            });
          });
        }
        
        // Filter results client-side for octaves
        // A piece matches if its octave range [min_octaves, max_octaves] includes any selected octave
        if (selectedOctaves.length > 0) {
          const octaveNums = selectedOctaves.map(o => parseInt(o)).filter(o => !isNaN(o));
          data = data.filter(piece => {
            // Convert to numbers, handling null/undefined/string cases
            const pieceMin = piece.min_octaves != null ? Number(piece.min_octaves) : null;
            const pieceMax = piece.max_octaves != null ? Number(piece.max_octaves) : null;
            
            // Handle null cases - if both are null, don't match
            if (pieceMin === null && pieceMax === null) return false;
            
            // If only one is null, treat it as a single-octave piece
            if (pieceMin === null && pieceMax !== null) {
              return octaveNums.includes(pieceMax);
            }
            if (pieceMax === null && pieceMin !== null) {
              return octaveNums.includes(pieceMin);
            }
            
            // Check if any selected octave falls within the piece's range
            return octaveNums.some(octave => {
              return pieceMin <= octave && pieceMax >= octave;
            });
          });
        }
        
        setPieces(data);
      }
    } catch (e) {
      console.error('Failed to search pieces:', e);
    }
  };

  const clearSearch = () => {
    setSearchForm({
      title: '',
      composer: '',
      arranger: '',
      publishers_number: '',
      level: '',
      ensemble: '',
      required_instruments: '',
      optional_instruments: '',
      comments: '',
      group: '',
      event: '',
      date: ''
    });
    setSelectedLevels([]);
    setSelectedOctaves([]);
    fetchPieces();
  };

  const handleInputChange = (field, value) => {
    setSearchForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      searchPieces();
    }
  };

  const handleLevelToggle = (level) => {
    setSelectedLevels(prev => 
      prev.includes(level) 
        ? prev.filter(l => l !== level)
        : [...prev, level]
    );
  };

  const handleOctaveToggle = (octave) => {
    setSelectedOctaves(prev => 
      prev.includes(octave) 
        ? prev.filter(o => o !== octave)
        : [...prev, octave]
    );
  };

  const formatOctaves = (min, max) => {
    if (min === max) return min?.toString() || '';
    if (!min && !max) return '';
    if (!min) return max?.toString() || '';
    if (!max) return min?.toString() || '';
    return `${min}-${max}`;
  };

  return (
    <div style={{ padding: '1.5rem 2rem', maxWidth: '1700px', margin: '0 auto' }}>
      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0 }}>Music Library</h1>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <Link to="/music/piece/new" style={{ padding: '10px 16px', backgroundColor: '#28a745', color: 'white', textDecoration: 'none', borderRadius: '5px', fontWeight: 'bold' }}>
            Add New Piece
          </Link>
          <Link to="/" style={{ padding: '10px 16px', backgroundColor: '#ffc107', color: 'black', textDecoration: 'none', borderRadius: '5px', fontWeight: 'bold' }}>
            Back to Home
          </Link>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '2.5rem' }}>
        {/* Left column - Search form */}
        <div style={{ flex: 1, minWidth: '400px' }}>
          <div style={{ background: '#f8f9fa', padding: '1.5rem', borderRadius: '10px', marginBottom: '1.5rem' }}>
            <h2 style={{ marginTop: 0, marginBottom: '1.5rem' }}>Search</h2>
            
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Title:</label>
                  <input
                    type="text"
                    value={searchForm.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Composer:</label>
                  <input
                    type="text"
                    value={searchForm.composer}
                    onChange={(e) => handleInputChange('composer', e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Arranger:</label>
                  <input
                    type="text"
                    value={searchForm.arranger}
                    onChange={(e) => handleInputChange('arranger', e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Publisher's Number:</label>
                  <input
                    type="text"
                    value={searchForm.publishers_number}
                    onChange={(e) => handleInputChange('publishers_number', e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Level:</label>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {[1, 2, 3, 4, 5, 6].map(level => (
                    <label key={level} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedLevels.includes(level.toString())}
                        onChange={() => handleLevelToggle(level.toString())}
                        style={{ marginRight: '4px' }}
                      />
                      {level}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Octaves:</label>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {[2, 3, 4, 5].map(octave => (
                    <label key={octave} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedOctaves.includes(octave.toString())}
                        onChange={() => handleOctaveToggle(octave.toString())}
                        style={{ marginRight: '4px' }}
                      />
                      {octave}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Ensemble:</label>
                <select
                  value={searchForm.ensemble}
                  onChange={(e) => handleInputChange('ensemble', e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
                >
                  <option value="">All</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5</option>
                  <option value="6">6</option>
                  <option value="12 bell">12 bell</option>
                  <option value="Porta-Carol">Porta-Carol</option>
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Required Instruments:</label>
                  <input
                    type="text"
                    value={searchForm.required_instruments}
                    onChange={(e) => handleInputChange('required_instruments', e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Optional Instruments:</label>
                  <input
                    type="text"
                    value={searchForm.optional_instruments}
                    onChange={(e) => handleInputChange('optional_instruments', e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Comments:</label>
                <input
                  type="text"
                  value={searchForm.comments}
                  onChange={(e) => handleInputChange('comments', e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Group:</label>
                  <select
                    value={searchForm.group}
                    onChange={(e) => handleInputChange('group', e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
                  >
                    <option value="">All</option>
                    <option value="Carillon">Carillon</option>
                    <option value="Carillon Ensemble">Carillon Ensemble</option>
                    <option value="Morning Glories">Morning Glories</option>
                    <option value="Marting To Glory">Marting To Glory</option>
                    <option value="Munchkins">Munchkins</option>
                    <option value="High School">High School</option>
                    <option value="12 Bell">12 Bell</option>
                    <option value="Patriots Day">Patriots Day</option>
                    <option value="Massed">Massed</option>
                    <option value="Bronze">Bronze</option>
                    <option value="Copper">Copper</option>
                    <option value="Tin">Tin</option>
                    <option value="Wedding">Wedding</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Event:</label>
                  <select
                    value={searchForm.event}
                    onChange={(e) => handleInputChange('event', e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
                  >
                    <option value="">All</option>
                    <option value="Festival">Festival</option>
                    <option value="Bell Sunday">Bell Sunday</option>
                    <option value="Maundy Thursday">Maundy Thursday</option>
                    <option value="Good Friday">Good Friday</option>
                    <option value="Easter">Easter</option>
                    <option value="Christmas Sunday">Christmas Sunday</option>
                    <option value="Advent">Advent</option>
                    <option value="Lent">Lent</option>
                    <option value="Spring Ring">Spring Ring</option>
                    <option value="HS Ring Out">HS Ring Out</option>
                    <option value="Patriots Day">Patriots Day</option>
                    <option value="Holiday Concert">Holiday Concert</option>
                    <option value="Weekday Holiday Concert">Weekday Holiday Concert</option>
                    <option value="Sunday Service">Sunday Service</option>
                    <option value="Wedding">Wedding</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Date:</label>
                <input
                  type="date"
                  value={searchForm.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  onClick={searchPieces}
                  style={{ padding: '10px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  Search
                </button>
                <button
                  onClick={clearSearch}
                  style={{ padding: '10px 20px', backgroundColor: '#ffc107', color: 'black', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right column - Results */}
        <div style={{ flex: 2, minWidth: '600px' }}>
          <h2>Results ({pieces.length} pieces)</h2>
          
          {loading ? (
            <div>Loading...</div>
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {pieces.map(piece => (
                <div key={piece.piece_id} style={{
                  padding: '1rem',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }} onClick={() => navigate(`/music/piece/${piece.piece_id}`)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                        {piece.title}
                      </div>
                      <div style={{ color: '#666', marginBottom: '0.5rem' }}>
                        {piece.composer && `Composer: ${piece.composer}`}
                        {piece.arranger && ` | Arranger: ${piece.arranger}`}
                      </div>
                      <div style={{ display: 'flex', gap: '1rem', fontSize: '0.9rem', color: '#666' }}>
                        {piece.level && <span>Level: {piece.level}</span>}
                        {piece.min_octaves && <span>Octaves: {formatOctaves(piece.min_octaves, piece.max_octaves)}</span>}
                        {piece.ensemble && <span>Ensemble: {piece.ensemble}</span>}
                        {piece.publishers_number && <span>Publisher: {piece.publishers_number}</span>}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: '0.9rem', color: '#666' }}>
                      {piece.performances && piece.performances.length > 0 && (
                        <div>{piece.performances.length} performance(s)</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PieceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [piece, setPiece] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [initialFormData, setInitialFormData] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showPerformanceDialog, setShowPerformanceDialog] = useState(false);
  const [newPerformance, setNewPerformance] = useState({
    group: '',
    date: '',
    event: ''
  });

  useTabTitle(piece?.title ? `${piece.title} - Music Library` : 'Music Library');

  useEffect(() => {
    if (id !== 'new') {
      fetchPiece();
    } else {
      setLoading(false);
      setEditing(true);
      const initialData = {
        title: '',
        title_sort: '',
        composer: '',
        arranger: '',
        num_copies: 1,
        publishers_number: '',
        level: '',
        min_octaves: '',
        max_octaves: '',
        ensemble: '',
        required_instruments: '',
        optional_instruments: '',
        comments: ''
      };
      setFormData(initialData);
      setInitialFormData(initialData);
      setHasUnsavedChanges(false);
    }
  }, [id]);

  // Track unsaved changes (both form data and performance data)
  useEffect(() => {
    let hasChanges = false;
    
    // Check form data changes
    if (editing && Object.keys(formData).length > 0 && Object.keys(initialFormData).length > 0) {
      hasChanges = JSON.stringify(formData) !== JSON.stringify(initialFormData);
    }
    
    // Check if there's unsaved performance data
    const hasUnsavedPerformance = !editing && (newPerformance.group || newPerformance.date);
    
    setHasUnsavedChanges(hasChanges || hasUnsavedPerformance);
  }, [formData, initialFormData, editing, newPerformance]);

  // Note: Browser back button handling is limited with BrowserRouter
  // We handle tab close/refresh with beforeunload and Link clicks with onClick handlers

  // Warn before leaving page with unsaved changes (handles tab close/refresh)
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      return e.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const fetchPiece = async () => {
    try {
      const response = await fetch(`${API_URL}/pieces/${id}`);
      if (response.ok) {
        const data = await response.json();
        setPiece(data);
        setFormData(data);
        setInitialFormData({ ...data }); // Store initial state for comparison
        setHasUnsavedChanges(false);
        // If we just created this piece, exit edit mode and scroll to performance section
        if (showPerformanceDialog) {
          setEditing(false);
          setShowPerformanceDialog(false); // Reset flag
          // Scroll to performance section after a brief delay to ensure DOM is updated
          setTimeout(() => {
            const perfSection = document.querySelector('[data-performance-section]');
            if (perfSection) {
              perfSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        }
      }
    } catch (e) {
      console.error('Failed to fetch piece:', e);
    } finally {
      setLoading(false);
    }
  };

  const computeTitleSort = (title) => {
    if (!title) return '';
    // Remove leading single or double quotes
    let processedTitle = title;
    while (processedTitle && (processedTitle.startsWith("'") || processedTitle.startsWith('"'))) {
      processedTitle = processedTitle.substring(1);
    }
    return processedTitle.toLowerCase().replace('the ', '').replace('a ', '').replace('an ', '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Compute title_sort if not provided
      const submitData = { ...formData };
      if (!submitData.title_sort || submitData.title_sort === '') {
        submitData.title_sort = computeTitleSort(submitData.title);
      }
      
      // Clean up data: convert empty strings to null for optional fields
      // Integer fields should be null or a number, not empty string
      if (submitData.min_octaves === '' || submitData.min_octaves === null) {
        submitData.min_octaves = null;
      } else if (typeof submitData.min_octaves === 'string') {
        submitData.min_octaves = parseInt(submitData.min_octaves) || null;
      }
      
      if (submitData.max_octaves === '' || submitData.max_octaves === null) {
        submitData.max_octaves = null;
      } else if (typeof submitData.max_octaves === 'string') {
        submitData.max_octaves = parseInt(submitData.max_octaves) || null;
      }
      
      // Convert empty strings to null for optional string fields
      const optionalStringFields = ['composer', 'arranger', 'publishers_number', 'level', 'ensemble', 'required_instruments', 'optional_instruments', 'comments'];
      optionalStringFields.forEach(field => {
        if (submitData[field] === '') {
          submitData[field] = null;
        }
      });
      
      const url = id === 'new' ? `${API_URL}/pieces` : `${API_URL}/pieces/${id}`;
      const method = id === 'new' ? 'POST' : 'PUT';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });
      
      if (response.ok) {
        const savedPiece = await response.json();
        setHasUnsavedChanges(false); // Clear unsaved changes flag
        if (id === 'new') {
          // Navigate to the new piece and show performance section
          navigate(`/music/piece/${savedPiece.piece_id}`);
          // The piece will be fetched and we'll exit edit mode to show performance form
          setShowPerformanceDialog(true);
        } else {
          setEditing(false);
          fetchPiece();
        }
      } else {
        // Log error details for debugging
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Failed to save piece:', response.status, errorData);
        alert(`Failed to save piece: ${errorData.detail || errorData.message || 'Unknown error'}`);
      }
    } catch (e) {
      console.error('Failed to save piece:', e);
      alert(`Failed to save piece: ${e.message}`);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this piece?')) return;
    
    try {
      const response = await fetch(`${API_URL}/pieces/${id}`, { method: 'DELETE' });
      if (response.ok) {
        navigate('/music');
      }
    } catch (e) {
      console.error('Failed to delete piece:', e);
    }
  };

  const addPerformance = async () => {
    try {
      if (!newPerformance.group || !newPerformance.date) return;
      
      // Send date as plain YYYY-MM-DD string to avoid timezone conversion issues
      // The backend will parse it as a date-only value at local midnight
      const response = await fetch(`${API_URL}/pieces/${id}/performances`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newPerformance,
          // Keep date as-is (YYYY-MM-DD format from date input)
          date: newPerformance.date || null
        })
      });
      
      if (response.ok) {
        // Clear performance form and unsaved changes flag
        setNewPerformance({ group: '', date: '', event: '' });
        setHasUnsavedChanges(false);
        fetchPiece();
      }
    } catch (e) {
      console.error('Failed to add performance:', e);
    }
  };

  const deletePerformance = async (performanceId) => {
    if (!window.confirm('Are you sure you want to delete this performance?')) return;
    
    try {
      const response = await fetch(`${API_URL}/performances/${performanceId}`, { method: 'DELETE' });
      if (response.ok) {
        fetchPiece();
      }
    } catch (e) {
      console.error('Failed to delete performance:', e);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown Date';
    const date = new Date(dateString);
    // Check if it's the sentinel date for unknown dates (1900-01-01)
    if (date.getFullYear() === 1900 && date.getMonth() === 0 && date.getDate() === 1) {
      return 'Unknown Date';
    }
    return date.toLocaleDateString();
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div style={{ padding: '1.5rem 2rem', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0 }}>
          {editing ? (id === 'new' ? 'Add New Piece' : 'Edit Piece') : piece?.title || 'Piece Details'}
        </h1>
        <div style={{ display: 'flex', gap: '1rem' }}>
          {!editing && (
            <button
              onClick={() => {
                setEditing(true);
                setInitialFormData({ ...formData }); // Store current state when starting to edit
              }}
              style={{ padding: '10px 16px', backgroundColor: '#ffc107', color: 'black', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
            >
              Edit
            </button>
          )}
          {!editing && (
            <button
              onClick={handleDelete}
              style={{ padding: '10px 16px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
            >
              Delete
            </button>
          )}
          <Link 
            to="/music" 
            onClick={(e) => {
              if (hasUnsavedChanges && !window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
                e.preventDefault();
              }
            }}
            style={{ padding: '10px 16px', backgroundColor: '#ffc107', color: 'black', textDecoration: 'none', borderRadius: '5px', fontWeight: 'bold' }}
          >
            Back to Music Library
          </Link>
        </div>
      </div>

      {editing ? (
        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Title:</label>
              <input
                type="text"
                value={formData.title || ''}
                onChange={(e) => {
                  setFormData({ ...formData, title: e.target.value });
                }}
                onBlur={(e) => {
                  // Auto-compute title_sort on blur if it's empty
                  // This prevents updating while the user is still typing
                  if (!formData.title_sort || formData.title_sort.trim() === '') {
                    const computedTitleSort = computeTitleSort(formData.title || '');
                    setFormData({ ...formData, title_sort: computedTitleSort });
                  }
                }}
                required
                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Title Sort:</label>
              <input
                type="text"
                value={formData.title_sort || ''}
                onChange={(e) => {
                  // User is manually editing title_sort, so mark it as manually set
                  setFormData({ ...formData, title_sort: e.target.value });
                }}
                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Composer:</label>
              <input
                type="text"
                value={formData.composer || ''}
                onChange={(e) => setFormData({ ...formData, composer: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Arranger:</label>
              <input
                type="text"
                value={formData.arranger || ''}
                onChange={(e) => setFormData({ ...formData, arranger: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Copies:</label>
              <input
                type="number"
                value={formData.num_copies || 1}
                onChange={(e) => setFormData({ ...formData, num_copies: parseInt(e.target.value) || 1 })}
                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Level:</label>
              <input
                type="text"
                value={formData.level || ''}
                onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Ensemble:</label>
              <select
                value={formData.ensemble || ''}
                onChange={(e) => setFormData({ ...formData, ensemble: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
              >
                <option value="">Select Ensemble</option>
                {[1, 2, 3, 4, 5, 6].map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
                <option value="12 bell">12 bell</option>
                <option value="Porta-Carol">Porta-Carol</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Min Octaves:</label>
              <input
                type="number"
                value={formData.min_octaves || ''}
                onChange={(e) => setFormData({ ...formData, min_octaves: parseInt(e.target.value) || null })}
                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Max Octaves:</label>
              <input
                type="number"
                value={formData.max_octaves || ''}
                onChange={(e) => setFormData({ ...formData, max_octaves: parseInt(e.target.value) || null })}
                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Publisher's Number:</label>
            <input
              type="text"
              value={formData.publishers_number || ''}
              onChange={(e) => setFormData({ ...formData, publishers_number: e.target.value })}
              style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Required Instruments:</label>
              <textarea
                value={formData.required_instruments || ''}
                onChange={(e) => setFormData({ ...formData, required_instruments: e.target.value })}
                rows={3}
                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Optional Instruments:</label>
              <textarea
                value={formData.optional_instruments || ''}
                onChange={(e) => setFormData({ ...formData, optional_instruments: e.target.value })}
                rows={3}
                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Comments:</label>
            <textarea
              value={formData.comments || ''}
              onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
              rows={4}
              style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="submit"
              style={{ padding: '12px 24px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
            >
              {id === 'new' ? 'Create Piece' : 'Update Piece'}
            </button>
            {editing && (
              <button
                type="button"
                onClick={() => {
                  if (hasUnsavedChanges && !window.confirm('You have unsaved changes. Are you sure you want to cancel editing?')) {
                    return;
                  }
                  setEditing(false);
                  setFormData(piece || initialFormData);
                  setHasUnsavedChanges(false);
                }}
                style={{ padding: '12px 24px', backgroundColor: '#ffc107', color: 'black', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      ) : (
        <div style={{ display: 'grid', gap: '2rem' }}>
          {/* Piece Details */}
          <div style={{ background: '#f8f9fa', padding: '1.5rem', borderRadius: '10px' }}>
            <h2>Piece Details</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div><strong>Title:</strong> {piece.title}</div>
              <div><strong>Composer:</strong> {piece.composer || 'N/A'}</div>
              <div><strong>Arranger:</strong> {piece.arranger || 'N/A'}</div>
              <div><strong>Level:</strong> {piece.level || 'N/A'}</div>
              <div><strong>Octaves:</strong> {piece.min_octaves && piece.max_octaves ? `${piece.min_octaves}-${piece.max_octaves}` : 'N/A'}</div>
              <div><strong>Ensemble:</strong> {piece.ensemble || 'N/A'}</div>
              <div><strong>Copies:</strong> {piece.num_copies}</div>
              <div><strong>Publisher's Number:</strong> {piece.publishers_number || 'N/A'}</div>
            </div>
            
            {piece.required_instruments && (
              <div style={{ marginTop: '1rem' }}>
                <strong>Required Instruments:</strong>
                <div style={{ whiteSpace: 'pre-wrap' }}>{piece.required_instruments}</div>
              </div>
            )}
            
            {piece.optional_instruments && (
              <div style={{ marginTop: '1rem' }}>
                <strong>Optional Instruments:</strong>
                <div style={{ whiteSpace: 'pre-wrap' }}>{piece.optional_instruments}</div>
              </div>
            )}
            
            {piece.comments && (
              <div style={{ marginTop: '1rem' }}>
                <strong>Comments:</strong>
                <div style={{ whiteSpace: 'pre-wrap' }}>{piece.comments}</div>
              </div>
            )}
          </div>

          {/* Performances */}
          <div data-performance-section style={{ background: '#f8f9fa', padding: '1.5rem', borderRadius: '10px' }}>
            <h2>Performance History</h2>
            
            {piece.performances && piece.performances.length > 0 ? (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {piece.performances.map(perf => (
                  <div key={perf.performance_id} style={{
                    padding: '1rem',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <strong>{perf.group || 'Unknown'}</strong> - {formatDate(perf.date)}
                      {perf.event && ` (${perf.event})`}
                    </div>
                    <button
                      onClick={() => deletePerformance(perf.performance_id)}
                      style={{ padding: '4px 8px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold' }}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p>No performances recorded yet.</p>
            )}

            {/* Add Performance Form */}
            <div style={{ marginTop: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '5px' }}>
              <h3>Add Performance</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', alignItems: 'end' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Group:</label>
                  <select
                    value={newPerformance.group}
                    onChange={(e) => setNewPerformance({ ...newPerformance, group: e.target.value })}
                    style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
                  >
                    <option value="">Select Group</option>
                    <option value="Carillon">Carillon</option>
                    <option value="Carillon Ensemble">Carillon Ensemble</option>
                    <option value="Morning Glories">Morning Glories</option>
                    <option value="Marting To Glory">Marting To Glory</option>
                    <option value="Munchkins">Munchkins</option>
                    <option value="High School">High School</option>
                    <option value="12 Bell">12 Bell</option>
                    <option value="Patriots Day">Patriots Day</option>
                    <option value="Massed">Massed</option>
                    <option value="Bronze">Bronze</option>
                    <option value="Copper">Copper</option>
                    <option value="Tin">Tin</option>
                    <option value="Wedding">Wedding</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Date:</label>
                  <input
                    type="date"
                    value={newPerformance.date}
                    onChange={(e) => setNewPerformance({ ...newPerformance, date: e.target.value })}
                    style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Event:</label>
                  <select
                    value={newPerformance.event}
                    onChange={(e) => setNewPerformance({ ...newPerformance, event: e.target.value })}
                    style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
                  >
                    <option value="">Select Event</option>
                    <option value="Festival">Festival</option>
                    <option value="Bell Sunday">Bell Sunday</option>
                    <option value="Maundy Thursday">Maundy Thursday</option>
                    <option value="Good Friday">Good Friday</option>
                    <option value="Easter">Easter</option>
                    <option value="Christmas Sunday">Christmas Sunday</option>
                    <option value="Advent">Advent</option>
                    <option value="Lent">Lent</option>
                    <option value="Spring Ring">Spring Ring</option>
                    <option value="HS Ring Out">HS Ring Out</option>
                    <option value="Patriots Day">Patriots Day</option>
                    <option value="Holiday Concert">Holiday Concert</option>
                    <option value="Weekday Holiday Concert">Weekday Holiday Concert</option>
                    <option value="Sunday Service">Sunday Service</option>
                    <option value="Wedding">Wedding</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <button
                onClick={addPerformance}
                disabled={!newPerformance.group || !newPerformance.date}
                style={{ 
                  marginTop: '1rem',
                  padding: '8px 16px', 
                  backgroundColor: newPerformance.group && newPerformance.date ? '#28a745' : '#cccccc', 
                  color: newPerformance.group && newPerformance.date ? 'white' : '#666666', 
                  border: 'none', 
                  borderRadius: '5px', 
                  cursor: newPerformance.group && newPerformance.date ? 'pointer' : 'not-allowed',
                  fontWeight: 'bold'
                }}
              >
                Add Performance
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MusicApp() {
  return (
    <Routes>
      <Route path="/" element={<MusicMainPage />} />
      <Route path="/piece/:id" element={<PieceDetailPage />} />
    </Routes>
  );
}

export default MusicApp; 